import { cva, type VariantProps } from 'class-variance-authority'
import React, { ElementType, HTMLAttributes, forwardRef } from 'react'
import { cn } from './cn'

export const textVariants = cva('m-0 font-text', {
  variants: {
    variant: {
      'body-l': 'text-[18px] leading-[1.6] font-normal',
      body: 'text-[16px] leading-[1.55] font-normal',
      'body-s': 'text-[14px] leading-[1.5] font-normal',
      label: 'text-[13px] leading-[1.4] font-medium tracking-[0.005em]',
      caption: 'text-[12px] leading-[1.4] font-medium tracking-[0.01em]',
      overline: 'font-mono text-[11px] leading-[1.3] font-semibold uppercase tracking-[0.12em]',
      mono: 'font-mono text-[14px] leading-[1.5] font-normal',
    },
    tone: {
      inherit: '',
      fg: 'text-fg',
      'fg-2': 'text-fg-2',
      'fg-3': 'text-fg-3',
      accent: 'text-accent',
      link: 'text-link',
      success: 'text-success',
      danger: 'text-danger',
      warning: 'text-warning',
      info: 'text-info',
    },
    weight: {
      regular: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    align: {
      start: 'text-left',
      center: 'text-center',
      end: 'text-right',
    },
    tabular: { true: 'tabular' },
    truncate: { true: 'truncate' },
    breakAll: { true: 'break-all' },
  },
  defaultVariants: { variant: 'body', tone: 'inherit' },
})

export interface TextProps
  extends Omit<HTMLAttributes<HTMLElement>, 'color'>,
    VariantProps<typeof textVariants> {
  as?: ElementType
  htmlFor?: string
}

/** Body copy. Pick `variant` by role and `tone` for colour; never pass colours directly. */
export const Text = forwardRef<HTMLElement, TextProps>(
  (
    {
      as: Comp = 'p',
      variant,
      tone,
      weight,
      align,
      tabular,
      truncate,
      breakAll,
      className,
      ...props
    },
    ref
  ) => (
    <Comp
      ref={ref}
      className={cn(
        textVariants({ variant, tone, weight, align, tabular, truncate, breakAll }),
        className
      )}
      {...props}
    />
  )
)
Text.displayName = 'Text'
